import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../../../API';
import { useCaseWorkspace } from '../../shared/hooks/useCaseWorkspace';
import styles from '../CaseBuilderEnhanced.module.css';

export default function CaseCreateWizard({ open, onClose, onCreated }) {
    const [step, setStep] = useState(0);
    const [localId, setLocalId] = useState('');
    const [naccId, setNaccId] = useState('');
    const [pullClinical, setPullClinical] = useState(true);
    const [protocolId, setProtocolId] = useState('');

    const workspace = useCaseWorkspace({ data: null, name: null });

    const { data: protocolsResponse } = useQuery({
        queryKey: ['bdsa-protocol'],
        queryFn: () => api.get('/bdsa/protocol'),
        staleTime: 5 * 60 * 1000,
        enabled: open,
    });

    const protocols = protocolsResponse?.status === 200 && Array.isArray(protocolsResponse.data)
        ? protocolsResponse.data
        : [];

    if (!open) return null;

    const reset = () => {
        setStep(0);
        setLocalId('');
        setNaccId('');
        setPullClinical(true);
        setProtocolId('');
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleCreate = async () => {
        const trimmed = localId.trim();
        if (!trimmed) return;
        const caseDoc = await workspace.handleCreateCase({
            localIdentifier: trimmed,
            naccId: pullClinical ? naccId : '',
            protocolId: protocolId || null,
        });
        if (caseDoc) {
            reset();
            onCreated(caseDoc);
        }
    };

    const steps = ['Identity', 'NACC (optional)', 'Protocol (optional)'];

    return (
        <div className={styles.dialogOverlay} role="dialog" aria-modal="true">
            <div className={styles.dialog} style={{ maxWidth: 520 }}>
                <h3 className={styles.dialogTitle}>Create new case</h3>

                <div className={styles.wizardSteps}>
                    {steps.map((label, i) => (
                        <div
                            key={label}
                            className={`${styles.wizardStep} ${i === step ? styles.wizardStepActive : ''}`}
                        >
                            {label}
                        </div>
                    ))}
                </div>

                {step === 0 && (
                    <div className={styles.formRow}>
                        <label className={styles.formLabel} htmlFor="wizard-local-id">
                            Local case ID
                        </label>
                        <input
                            id="wizard-local-id"
                            type="text"
                            className={styles.input}
                            value={localId}
                            onChange={(e) => setLocalId(e.target.value)}
                            placeholder="Required"
                            autoFocus
                        />
                    </div>
                )}

                {step === 1 && (
                    <>
                        <div className={styles.formRow}>
                            <label className={styles.formLabel} htmlFor="wizard-nacc-id">
                                NACC ID (optional)
                            </label>
                            <input
                                id="wizard-nacc-id"
                                type="text"
                                className={styles.input}
                                value={naccId}
                                onChange={(e) => setNaccId(e.target.value)}
                                placeholder="e.g. NACC876161"
                            />
                        </div>
                        <label className={styles.formRow}>
                            <input
                                type="checkbox"
                                checked={pullClinical}
                                onChange={(e) => setPullClinical(e.target.checked)}
                            />
                            {' '}
                            Pull clinical metadata from NACC when creating
                        </label>
                    </>
                )}

                {step === 2 && (
                    <div className={styles.formRow}>
                        <label className={styles.formLabel} htmlFor="wizard-protocol">
                            Sectioning protocol (optional)
                        </label>
                        <select
                            id="wizard-protocol"
                            className={styles.select}
                            value={protocolId}
                            onChange={(e) => setProtocolId(e.target.value)}
                        >
                            <option value="">None — define blocks manually</option>
                            {protocols.map((p) => (
                                <option key={p._id} value={p._id}>{p.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {workspace.naccClinicalStatus && (
                    <p className={workspace.naccClinicalStatus.ok ? styles.statusOk : styles.statusError}>
                        {workspace.naccClinicalStatus.message}
                    </p>
                )}

                <div className={styles.dialogActions}>
                    <button type="button" className={styles.buttonSecondary} onClick={handleClose}>
                        Cancel
                    </button>
                    {step > 0 && (
                        <button type="button" className={styles.buttonSecondary} onClick={() => setStep(step - 1)}>
                            Back
                        </button>
                    )}
                    {step < steps.length - 1 ? (
                        <button
                            type="button"
                            className={styles.buttonPrimary}
                            disabled={step === 0 && !localId.trim()}
                            onClick={() => setStep(step + 1)}
                        >
                            Next
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={styles.buttonPrimary}
                            disabled={workspace.createPending || !localId.trim()}
                            onClick={handleCreate}
                        >
                            {workspace.createPending ? 'Creating…' : 'Create case'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
